import { Pagination } from '@teko/ds-desktop';
import { Col, Flex, Title, styled } from '@teko/ds-mobile';
import { FilterOption } from '@teko/types/ecommerce';
import { cleanObject } from '@teko/utils';
import { HamperGrid } from 'components/desktop/organisms/CustomHamper/hamperComponent';
import { BuildPCFilterMobile } from 'components/mobile/organisms/BuildPC/BuildPCFilterMobile';
import {
  DEFAULT_PRICE_FILTER,
  HAMPER_PAGE_SIZE,
  ITEM_PER_PAGE_IN_LISTING_PAGE,
} from 'constants/index';
import {
  convertDiscoveryFilter,
  convertFilter,
  convertToPayloadSorting,
} from 'helpers';
import { useHamperProducts, usePos365StoreCode, useTenantConfig } from 'hooks';
import { t } from 'i18n';
import {
  SortOptionInterface,
  cookFilterPrice,
  extractSortQuery,
} from 'modules/search-page/shared';
import dynamic from 'next/dynamic';
import React, { useMemo, useState } from 'react';

const ProductList = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductList),
);

const NotFound = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.NoProductsFound),
);

type ProductCardType = 'grid' | 'list';

type HamperModalComponentProps = {
  collectionSlug: string;
  hamperItemKey: string;
  keyword: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  activeOptions: FilterOption[];
  activeSortOption?: SortOptionInterface;
  priceFilterValue?: {
    gte: number;
    lte: number;
  };
  setActiveSortOption: (activeSortOption?: SortOptionInterface) => void;
  setActiveOptions: (activeOptions: FilterOption[]) => void;
  setPriceFilterValue: (priceFilterValue: { gte: number; lte: number }) => void;
};

export const HamperModalComponent: React.FC<HamperModalComponentProps> = (
  props,
) => {
  const {
    collectionSlug,
    keyword,
    currentPage,
    hamperItemKey,
    onPageChange,
    activeOptions,
    activeSortOption,
    priceFilterValue,
    setActiveSortOption,
    setActiveOptions,
    setPriceFilterValue,
  } = props;

  const [productCardType, setProductCardType] =
    useState<ProductCardType>('grid');

  const { pos365StoreCode } = usePos365StoreCode();
  const { contactLink } = useTenantConfig();

  const [sort, order] = activeSortOption
    ? extractSortQuery(activeSortOption.optionCode)
    : [undefined, undefined];

  // fetch filter block data
  const { filter, loading: filterLoading } = useHamperProducts({
    slug: collectionSlug,
    returnFilterable: [
      'FILTER_TYPE_ATTRIBUTE',
      'FILTER_TYPE_BRAND',
      'FILTER_TYPE_PRICE',
      'FILTER_TYPE_CLEARANCE',
    ],
    pageSize: 1,
  });

  // fetch product list
  const {
    products,
    pagination,
    loading: isLoading,
  } = useHamperProducts(
    {
      slug: collectionSlug,
      query: keyword,
      sorting: convertToPayloadSorting(sort, order),
      filter: cleanObject({
        ...convertFilter(activeOptions),
        priceGte: priceFilterValue?.gte,
        priceLte: priceFilterValue?.lte,
      }),
      pageSize: HAMPER_PAGE_SIZE,
      page: currentPage,
    },
    hamperItemKey,
  );

  const filters = useMemo(() => {
    return !!filter ? convertDiscoveryFilter(filter) : [];
  }, [filter]);

  const priceFilter = useMemo(() => {
    return cookFilterPrice(filter?.priceLte, filter?.priceGte);
  }, [filter?.priceLte, filter?.priceGte]);

  const onSortOptionSelected = (sortOption: SortOptionInterface) => {
    if (sortOption.optionCode === activeSortOption?.optionCode) {
      setActiveSortOption(undefined);
    } else {
      setActiveSortOption(sortOption);
    }
  };

  const onSubmit = (
    activeFilters: FilterOption[],
    filterPriceValue: {
      gte: number;
      lte: number;
    },
  ) => {
    setActiveOptions(activeFilters);
    setPriceFilterValue(filterPriceValue);
  };

  const onClearFilters = () =>
    onSubmit([], {
      gte: priceFilter.min,
      lte: priceFilter.max,
    });

  return (
    <>
      <PageTitleContainer justify="start" align="center">
        <Col style={{ maxWidth: '90%' }}>
          <PageTitle>{t('Chọn sản phẩm')}</PageTitle>
        </Col>
        {pagination?.totalItems != undefined ? (
          <Col marginLeft="s4">
            <Title
              color="textSecondary"
              weight="regular"
              style={{ fontSize: '0.75rem', lineHeight: '0.75rem' }}
            >
              {t('({{quantity}} sản phẩm)', {
                quantity: pagination?.totalItems,
              })}
            </Title>
          </Col>
        ) : null}
      </PageTitleContainer>
      <BuildPCFilterMobile
        loading={filterLoading}
        onClickLayout={(): void =>
          setProductCardType(productCardType === 'grid' ? 'list' : 'grid')
        }
        layoutType={pos365StoreCode ? 'list' : productCardType}
        filters={filters}
        filterPrice={{
          ...priceFilter,
          value: priceFilterValue || DEFAULT_PRICE_FILTER,
        }}
        activeSortOption={activeSortOption}
        activeOptions={activeOptions}
        onSortOptionSelected={onSortOptionSelected}
        onSubmit={onSubmit}
        onClearFilters={onClearFilters}
      />
      {pos365StoreCode ? (
        <StyledProductList
          products={products}
          contactLink={contactLink}
          itemsPerPage={
            !!pagination
              ? Math.ceil(pagination?.totalItems / pagination?.totalPages)
              : ITEM_PER_PAGE_IN_LISTING_PAGE
          }
        />
      ) : (
        <StyledHamperGrid
          hamperItemKey={hamperItemKey}
          type={productCardType}
          products={products}
          contactLink={contactLink}
          productsPerRow={2}
          itemsPerPage={
            !!pagination
              ? Math.ceil(pagination?.totalItems / pagination?.totalPages)
              : ITEM_PER_PAGE_IN_LISTING_PAGE
          }
        />
      )}

      {!isLoading && products?.length === 0 && <NotFound show />}
      {!!pagination ? (
        <StyledFooter justify="center" align="center">
          <StyledPagination
            height="50px"
            pageNeighbor={1}
            totalRecords={pagination?.totalItems}
            currentPage={currentPage}
            onPageChange={onPageChange}
          />
        </StyledFooter>
      ) : null}
    </>
  );
};

const PageTitleContainer = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.background};
  padding: 0.5rem 0.75rem;
  position: sticky;
  top: 0px;
  z-index: 1040;
  height: 40px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1rem;
  line-height: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledFooter = styled(Flex)`
  position: sticky;
  padding: 0 16px;
  bottom: 0px;
  background-color: white;
`;

const StyledPagination = styled(Pagination)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  > :first-child {
    width: 100%;
    justify-content: center;
  }
`;

const StyledHamperGrid = styled(HamperGrid)`
  height: calc(
    100% - 40px - 40px - 50px
  ); // minus the height of title container, filters & pagination
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledProductList = styled(ProductList)`
  height: calc(
    100% - 40px - 40px - 50px
  ); // minus the height of title container, filters & pagination
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;
